import React, { useCallback, useEffect, useMemo } from 'react';
import { connect } from 'react-redux';
import { withTranslation } from 'react-i18next';

import { makeGetSortedActiveProjectMilestones, makeGetSortedActiveProjectLists } from 'selectors/milestones';
import { makeGetLoadingState, makeGetFetchedState } from 'selectors/global';

import { fetchMilestones } from 'actions/milestones';

import ResourceSelector from 'components/ResourceSelector';
import DateTime from 'components/DateTime';
import MilestoneName from 'components/milestone-name';
import Select from 'components/select';

const MilestoneSelector = ({ label, isList, lists, excluded, selected, resourceCreateText, projectId, milestonesFetched, milestonesLoading, useSelect, autoSelectFirst, t, onSelect, onResourceCreate }) => {
    const attemptFetch = useCallback(() => {
        if (!milestonesFetched && !milestonesLoading && projectId) {
            fetchMilestones({ projectId });
        }
    }, [milestonesLoading, milestonesFetched, projectId]);

    useEffect(() => {
        attemptFetch();
    }, [attemptFetch]);

    const availableLists = useMemo(() => {
        if (excluded.length) {
            return lists.filter(({ id }) => !excluded.includes(id));
        }

        return lists;
    }, [lists, excluded]);

    useEffect(() => {
        if (autoSelectFirst && !selected && availableLists.length) {
            onSelect({
                id: availableLists[0].id
            });
        }
    }, [autoSelectFirst, selected, availableLists]);

    if (useSelect) {
        return (
            <Select
                label={label}
                placeholder={ t('Select a list') }
                withSearch={true}
                emptyText={ isList ? t('No Lists available') : t('No Milestones available') }
                options={availableLists.map(list => ({ id: list.id, text: list.name, color: list.color }))}
                selected={selected}
                onChange={onSelect}
            />
        )
    }

    return (
        <ResourceSelector
            withSearch={true}
            loading={!milestonesFetched}
            searchPlaceholder={ isList ? t('Search lists…') : t('Search milestones…') }
            emptyText={ isList ? t('No Lists available') : t('No Milestones available') }
            items={availableLists.map(list => ({
                id: list.id,
                title: <MilestoneName id={list.id} />,
                searchTitle: list.name,
                color: list.color,
                meta: !list.is_list ? (
                    <React.Fragment>
                        <DateTime format="date">{list.start}</DateTime>
                        &nbsp;-&nbsp;
                        <DateTime format="date">{list.end}</DateTime>
                    </React.Fragment>
                ) : null
            }))}
            selected={selected}
            resourceCreateText={resourceCreateText}
            onSelect={onSelect}
            onResourceCreate={onResourceCreate}
        />
    );
}

MilestoneSelector.defaultProps = {
    excluded: []
}

const makeMapStateToProps = () => {
    const getSortedActiveProjectMilestones = makeGetSortedActiveProjectMilestones();
    const getSortedActiveProjectLists = makeGetSortedActiveProjectLists();
    const getMilestonesLoading = makeGetLoadingState();
    const getMilestonesFetched = makeGetFetchedState();

    return ({ milestones }, ownProps) => {
        return {
            lists: ownProps.isList ? getSortedActiveProjectLists(milestones.items, ownProps.projectId) : getSortedActiveProjectMilestones(milestones.items, ownProps.projectId),
            milestonesLoading: getMilestonesLoading(milestones.fetchInfo, 'project', ownProps.projectId),
            milestonesFetched: getMilestonesFetched(milestones.fetchInfo, 'project', ownProps.projectId)
        }
    }
}

export default connect(makeMapStateToProps)(withTranslation('milestone')(MilestoneSelector));
